'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { usePathname } from '@/i18n/routing';

import { useAnalytics } from './useAnalytics';

export const usePageTracking = () => {
  const { page, track } = useAnalytics();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastPathRef = useRef<string | null>(null);

  useEffect(() => {
    const search = searchParams.toString();
    const currentPath = search ? `${pathname}?${search}` : pathname;

    if (lastPathRef.current && lastPathRef.current !== currentPath) {
      const [oldPath, oldSearch = ''] = lastPathRef.current.split('?');

      track({
        type: 'custom',
        eventName: 'page_leave',
        eventData: {
          surface: {
            path: oldPath,
            search: oldSearch,
          },
        },
      });
    }

    page({
      name: pathname,
      properties: {
        surface: {
          path: pathname,
          search,
        },
      },
    });

    lastPathRef.current = currentPath;
  }, [pathname, searchParams, page, track]);

  useEffect(() => {
    const handleLeave = () => {
      if (!lastPathRef.current) return;

      const [oldPath, oldSearch = ''] = lastPathRef.current.split('?');

      track({
        type: 'custom',
        eventName: 'page_leave',
        eventData: {
          surface: {
            path: oldPath,
            search: oldSearch,
          },
        },
      });
    };

    window.addEventListener('beforeunload', handleLeave);

    return () => {
      window.removeEventListener('beforeunload', handleLeave);
    };
  }, [track]);
};
